import React, { useMemo } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAutoDragScroll } from "../../utils/useAutoDragScroll";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Helper to shuffle an array
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const OnSaleShowcase = ({ onSaleData }) => {
  const navigate = useNavigate();
  const { ref, node, triggerPause } = useAutoDragScroll({ speed: 0.5, pauseDelay: 2000 });

  // Randomly pick 1 product from each brand's top 5, and shuffle the brands
  const showcaseProducts = useMemo(() => {
    if (!onSaleData) return [];

    let products = [];
    Object.keys(onSaleData).forEach(brand => {
      const brandProducts = onSaleData[brand];
      if (brandProducts && brandProducts.length > 0) {
        // Pick random product from the available ones
        const randomProduct = brandProducts[Math.floor(Math.random() * brandProducts.length)];
        products.push({ ...randomProduct, showcaseBrand: brand });
      }
    });

    return shuffleArray(products);
  }, [onSaleData]);

  // Triplicate for infinite scroll
  const displayProducts = [...showcaseProducts, ...showcaseProducts, ...showcaseProducts];

  if (!showcaseProducts.length) return null;

  return (
    <Box sx={{ mt: 3, mb: 4, px: { xs: 2, sm: 3 }, overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1.5, sm: 0 }, mb: 2 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: "'Lora', serif",
              fontSize: { xs: "18px", md: "28px" },
              fontWeight: 500,
              color: "#111",
              mb: 0.5,
              letterSpacing: "-0.02em"
            }}
          >
            <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>Deals</Box> you won't see anywhere else
          </Typography>
        </Box>
        <Typography
          onClick={() => navigate("/brands/on-sale")}
          sx={{
            fontFamily: "Poppins-Medium",
            fontSize: { xs: "12px", md: "14px" },
            color: "#5abccb",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": { textDecoration: "underline" },
            alignSelf: { xs: "flex-end", sm: "auto" }
          }}
        >
          View All On Sale <ArrowForwardIosIcon sx={{ fontSize: "10px" }} />
        </Typography>
      </Box>

      {/* Horizontal Slider */}
      <Box
        ref={ref}
        onMouseEnter={() => triggerPause(true)}
        onMouseLeave={() => triggerPause(false)}
        onTouchStart={() => triggerPause(true)}
        onTouchEnd={() => triggerPause(false)}
        sx={{
          display: "flex",
          gap: { xs: 1.5, md: 2.5 },
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
          px: 0.5,
          py: 1
        }}
      >
        {displayProducts.map((product, index) => (
          <Box
            key={`${product.product_id}-${index}`}
            onClick={() => {
              const slug = product.showcaseBrand.replace(/\s+/g, '-');
              navigate(`/on-sale/${encodeURIComponent(slug)}`);
            }}
            sx={{
              minWidth: { xs: "160px", sm: "200px", md: "240px" },
              display: "flex",
              flexDirection: "column",
              gap: 1,
              cursor: "pointer",
              "&:hover .image-container": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Box
              className="image-container"
              sx={{
                width: "100%",
                aspectRatio: "2/3",
                borderRadius: "12px",
                overflow: "hidden",
                position: "relative",
                transition: "all 0.3s ease",
                backgroundColor: "#F9F9F9",
              }}
            >
              <img
                src={product?.image_url}
                alt={product.title}
                referrerPolicy="no-referrer"
                loading="lazy"
                onError={(e) => {
                  const fallbackImage = product?.images?.[0];
                  if (fallbackImage && !e.target.dataset.triedFallback) {
                    e.target.dataset.triedFallback = "true";
                    e.target.src = fallbackImage;
                  } else {
                    e.target.onerror = null;
                    e.target.src = require("../../assets/images/no_image.png");
                  }
                }}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              {/* Discount Badge */}
              {product.price?.discounted && product.price?.original && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    background: "#FF3B30",
                    color: "#FFF",
                    px: 1,
                    py: 0.5,
                    borderRadius: "4px",
                    fontFamily: "Poppins-Bold",
                    fontSize: "10px",
                    letterSpacing: "0.5px"
                  }}
                >
                  SALE
                </Box>
              )}

              {/* Brand & Product Name Overlay */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
                  px: 2,
                  pt: 4,
                  pb: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins-SemiBold",
                    fontSize: { xs: "13px", sm: "14px" },
                    color: "#FFF",
                    letterSpacing: "0.2px",
                    textTransform: "uppercase"
                  }}
                >
                  {product.showcaseBrand}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins-Regular",
                    fontSize: { xs: "12px", sm: "13px" },
                    color: "rgba(255,255,255,0.9)",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {product.title}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
                  {product.price?.discounted ? (
                    <>
                      <Typography
                        sx={{
                          fontFamily: "Poppins-SemiBold",
                          fontSize: { xs: "12px", sm: "13px" },
                          color: "#FF5252",
                        }}
                      >
                        {product.price.discounted} {product.price.currency}
                      </Typography>
                      {product.price.original && (
                        <Typography
                          sx={{
                            fontFamily: "Poppins-Regular",
                            fontSize: { xs: "10px", sm: "11px" },
                            color: "rgba(255,255,255,0.6)",
                            textDecoration: "line-through",
                          }}
                        >
                          {product.price.original} {product.price.currency}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: "Poppins-SemiBold",
                        fontSize: { xs: "12px", sm: "13px" },
                        color: "#FFF",
                      }}
                    >
                      {product.price?.original || "N/A"} {product.price?.currency || ""}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default OnSaleShowcase;
